const URL_CAMARA = (import.meta.env.VITE_CAMERA_URL || 'https://fax-camara.enflujo.com').replace(/\/$/, '');
const MAX_ESPERA_REINTENTO = 15_000;
const MAX_ESPERA_PRIMER_CUADRO = 12_000;
const INTERVALO_SALUD = 10_000;
const MAX_ESPERA_SALUD = 5_000;

interface SaludCamara {
  ok: boolean;
}

export default () => {
  const transmision = document.getElementById('transmision') as HTMLImageElement;
  const estado = document.getElementById('estadoCamara') as HTMLDivElement;
  let temporizadorReintento: number | undefined;
  let temporizadorCarga: number | undefined;
  let temporizadorSalud: number | undefined;
  let consultaSalud: AbortController | undefined;
  let fallos = 0;
  let generacion = 0;
  let pausada = false;
  let conectada = false;

  const activa = () => !document.hidden && !pausada;

  function cambiarEstado(mensaje: string, disponible: boolean) {
    estado.textContent = mensaje;
    estado.classList.toggle('oculto', disponible);
    transmision.classList.toggle('sinSenal', !disponible);
  }

  function cerrarTransmision() {
    generacion += 1;
    conectada = false;
    window.clearTimeout(temporizadorCarga);
    transmision.onload = null;
    transmision.onerror = null;
    transmision.removeAttribute('src');
  }

  function programarReintento(delay: number) {
    window.clearTimeout(temporizadorReintento);
    if (!activa()) return;
    temporizadorReintento = window.setTimeout(() => {
      temporizadorReintento = undefined;
      conectar();
    }, delay);
  }

  function registrarFallo(intento: number) {
    if (intento !== generacion || !activa()) return;
    cerrarTransmision();
    fallos += 1;
    cambiarEstado('Reconectando cámara…', false);
    programarReintento(Math.min(1_000 * 2 ** (fallos - 1), MAX_ESPERA_REINTENTO));
  }

  function conectar() {
    if (!activa() || transmision.hasAttribute('src')) return;

    const intento = ++generacion;
    cambiarEstado(fallos ? 'Reconectando cámara…' : 'Conectando cámara…', false);

    transmision.onload = () => {
      if (intento !== generacion) return;
      window.clearTimeout(temporizadorCarga);
      conectada = true;
      fallos = 0;
      cambiarEstado('', true);
    };

    transmision.onerror = () => registrarFallo(intento);
    temporizadorCarga = window.setTimeout(() => registrarFallo(intento), MAX_ESPERA_PRIMER_CUADRO);
    transmision.src = `${URL_CAMARA}/stream.mjpg?t=${Date.now()}`;
  }

  function programarSalud(delay = INTERVALO_SALUD) {
    window.clearTimeout(temporizadorSalud);
    if (activa()) temporizadorSalud = window.setTimeout(comprobarSalud, delay);
  }

  async function comprobarSalud() {
    if (!activa()) return;

    consultaSalud?.abort();
    const controlador = new AbortController();
    consultaSalud = controlador;
    const limite = window.setTimeout(() => controlador.abort(), MAX_ESPERA_SALUD);

    try {
      const respuesta = await fetch(`${URL_CAMARA}/health?t=${Date.now()}`, {
        cache: 'no-store',
        signal: controlador.signal,
      });
      const salud = (await respuesta.json()) as SaludCamara;
      if (consultaSalud !== controlador) return;

      if (!respuesta.ok || !salud.ok) {
        if (transmision.hasAttribute('src')) registrarFallo(generacion);
        else cambiarEstado('Reconectando cámara…', false);
      } else if (!conectada && !transmision.hasAttribute('src') && temporizadorReintento === undefined) {
        programarReintento(0);
      }
    } catch {
      if (consultaSalud !== controlador || !activa()) return;
      if (transmision.hasAttribute('src')) registrarFallo(generacion);
      else cambiarEstado('Reconectando cámara…', false);
    } finally {
      window.clearTimeout(limite);
      if (consultaSalud === controlador) {
        consultaSalud = undefined;
        programarSalud();
      }
    }
  }

  function detener() {
    window.clearTimeout(temporizadorReintento);
    temporizadorReintento = undefined;
    window.clearTimeout(temporizadorSalud);
    consultaSalud?.abort();
    consultaSalud = undefined;
    cerrarTransmision();
  }

  function iniciar() {
    detener();
    fallos = 0;
    programarReintento(0);
    programarSalud(0);
  }

  document.body.addEventListener('pausarCamara', () => {
    pausada = true;
    detener();
  });
  document.body.addEventListener('reanudarCamara', () => {
    pausada = false;
    iniciar();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) detener();
    else iniciar();
  });
  window.addEventListener('online', iniciar);
  window.addEventListener('offline', () => {
    detener();
    cambiarEstado('Sin conexión. Esperando red…', false);
  });
  window.addEventListener('beforeunload', detener);

  cambiarEstado('Conectando cámara…', false);
  iniciar();
};
